/**
 * Settings → Training. A company-wide admin configures their company's weekly
 * training-digest here: whether it sends, when (schedule), what it reports, and who
 * receives it. A company with no row of its own inherits the fleet-wide default
 * (shown with an "inheriting the default" note) until the first save creates its
 * row; a superadmin with no company scope edits the fleet default directly.
 *
 * The weekly digest itself (the email + cron that consumes these settings) is TR-7;
 * this page only stores the configuration. Server owns authz + validation — a
 * caller who isn't a company-wide admin / superadmin gets a 403, shown as a notice.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GraduationCap, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EmailListInput } from '@/components/ui/EmailListInput';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getTrainingSettings,
  saveTrainingSettings,
  type TrainingSettingsRow,
  type TrainingSettingsPatch,
} from '@/lib/trainingSettingsApi';

const WEEKDAYS = [
  { v: '0', label: 'Sunday' },
  { v: '1', label: 'Monday' },
  { v: '2', label: 'Tuesday' },
  { v: '3', label: 'Wednesday' },
  { v: '4', label: 'Thursday' },
  { v: '5', label: 'Friday' },
  { v: '6', label: 'Saturday' },
];

/** A labelled toggle row. */
function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </h3>
  );
}

export function TrainingSettings() {
  const [row, setRow] = useState<TrainingSettingsRow | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [source, setSource] = useState<'company' | 'default'>('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const res = await getTrainingSettings();
      setRow(res.effective);
      setCompanyId(res.company_id);
      setSource(res.source);
      setEmails([...new Set((res.effective.recipient_emails || []).map((e) => e.trim().toLowerCase()).filter(Boolean))]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (/admin|forbidden|superadmin/i.test(msg)) setForbidden(true);
      else toast.error(`Could not load training settings: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const set = <K extends keyof TrainingSettingsRow>(key: K, value: TrainingSettingsRow[K]) =>
    setRow((r) => (r ? { ...r, [key]: value } : r));

  // send_time comes back as HH:MM:SS; the <input type=time> wants HH:MM.
  const timeValue = useMemo(() => (row?.send_time || '17:00:00').slice(0, 5), [row?.send_time]);

  const save = async () => {
    if (!row) return;
    const patch: TrainingSettingsPatch = {
      enabled: row.enabled,
      timezone: row.timezone,
      send_weekday: row.send_weekday,
      send_time: timeValue,
      report_passed: row.report_passed,
      report_threshold: row.report_threshold,
      threshold_pct: row.threshold_pct,
      report_all_tests_complete: row.report_all_tests_complete,
      report_last_used: row.report_last_used,
      inactivity_days: row.inactivity_days,
      notify_company_admins: row.notify_company_admins,
      notify_superadmins: row.notify_superadmins,
      recipient_emails: emails,
    };
    // No company scope (null) → we are editing the fleet-wide default (superadmin).
    const scope = companyId ? 'company' : 'default';
    setSaving(true);
    try {
      const res = await saveTrainingSettings(patch, scope);
      setRow(res.effective);
      // Reflect what actually happened: a company save now has its own row
      // ('company'); a fleet-default edit stays 'default'.
      setSource(res.scope === 'company' ? 'company' : 'default');
      setEmails([...new Set((res.effective.recipient_emails || []).map((e) => e.trim().toLowerCase()).filter(Boolean))]);
      toast.success('Training settings saved.');
    } catch (err) {
      toast.error(`Could not save: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-10 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (forbidden || !row) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Training settings are available to company-wide admins and superadmins.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">
      <div className="mb-4 flex items-center gap-2">
        <GraduationCap className="h-5 w-5 text-[#8FB560]" />
        <h2 className="text-lg font-semibold text-foreground">Training</h2>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-muted-foreground">
        Configure the weekly training digest for your team — when it sends, what it reports, and
        who receives it.
        {source === 'default' && companyId && (
          <span className="mt-1 block text-xs text-[#8FB560]">
            Your company is currently inheriting the fleet-wide default. Saving creates settings
            just for your company.
          </span>
        )}
        {!companyId && (
          <span className="mt-1 block text-xs text-[#8FB560]">
            Editing the fleet-wide default — every company without its own settings inherits this.
          </span>
        )}
      </p>

      <div className="max-w-2xl space-y-6">
        {/* Schedule */}
        <Card>
          <CardContent className="pt-6">
            <SectionTitle>Weekly digest</SectionTitle>
            <ToggleRow
              label="Send the weekly training digest"
              hint="A summary of your team's progress, emailed on the schedule below."
              checked={row.enabled}
              onChange={(v) => set('enabled', v)}
            />
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="ts-day">Day</Label>
                <Select
                  value={String(row.send_weekday)}
                  onValueChange={(v) => set('send_weekday', Number(v))}
                >
                  <SelectTrigger id="ts-day" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((d) => (
                      <SelectItem key={d.v} value={d.v}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ts-time">Time</Label>
                <Input
                  id="ts-time"
                  type="time"
                  className="mt-1"
                  value={timeValue}
                  onChange={(e) => set('send_time', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="ts-tz">Timezone</Label>
                <Input
                  id="ts-tz"
                  className="mt-1"
                  value={row.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                  placeholder="America/Los_Angeles"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report options */}
        <Card>
          <CardContent className="pt-6">
            <SectionTitle>What the digest reports</SectionTitle>
            <ToggleRow
              label="Who has passed each test"
              checked={row.report_passed}
              onChange={(v) => set('report_passed', v)}
            />
            <ToggleRow
              label="Who is below a score threshold"
              hint="Flag team members whose recent scores fall below the mark."
              checked={row.report_threshold}
              onChange={(v) => set('report_threshold', v)}
            />
            {row.report_threshold && (
              <div className="mb-2 flex items-center gap-2 pl-1">
                <Label htmlFor="ts-threshold" className="text-xs text-muted-foreground">
                  Threshold
                </Label>
                <Input
                  id="ts-threshold"
                  type="number"
                  min={1}
                  max={100}
                  className="h-8 w-20"
                  value={row.threshold_pct}
                  onChange={(e) => set('threshold_pct', Number(e.target.value))}
                />
                <span className="text-xs text-muted-foreground">%</span>
              </div>
            )}
            <ToggleRow
              label="Who has completed all three tests"
              checked={row.report_all_tests_complete}
              onChange={(v) => set('report_all_tests_complete', v)}
            />
            <ToggleRow
              label="Who hasn't trained recently"
              hint="Flag team members inactive for the window below."
              checked={row.report_last_used}
              onChange={(v) => set('report_last_used', v)}
            />
            {row.report_last_used && (
              <div className="mb-2 flex items-center gap-2 pl-1">
                <Label htmlFor="ts-inactivity" className="text-xs text-muted-foreground">
                  Inactive after
                </Label>
                <Input
                  id="ts-inactivity"
                  type="number"
                  min={1}
                  max={365}
                  className="h-8 w-20"
                  value={row.inactivity_days}
                  onChange={(e) => set('inactivity_days', Number(e.target.value))}
                />
                <span className="text-xs text-muted-foreground">days</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recipients */}
        <Card>
          <CardContent className="pt-6">
            <SectionTitle>Recipients</SectionTitle>
            <ToggleRow
              label="Company admins"
              hint="Everyone with company-wide admin access receives it."
              checked={row.notify_company_admins}
              onChange={(v) => set('notify_company_admins', v)}
            />
            <ToggleRow
              label="Superadmins"
              checked={row.notify_superadmins}
              onChange={(v) => set('notify_superadmins', v)}
            />
            <div className="mt-3">
              <Label htmlFor="ts-emails">Additional email addresses</Label>
              <EmailListInput id="ts-emails" className="mt-1" value={emails} onChange={setEmails} />
              <p className="mt-1 text-xs text-muted-foreground">
                Type or paste addresses. Per-person performance data leaves the app to these
                addresses.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
